/**
 * React Query hooks for farmer image uploads.
 * React Query hooks for the corresponding entity.for queries.
 */

import { QueryClient, useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import type { FarmerImageUpload } from "./farmerImageUploadTypes";
import { farmerImageUploadApi } from "./farmerImageUploadTypes";

/**
 * Fetch a single farmer image upload by ID
 * Handles errors gracefully - returns null if fetch fails instead of throwing
 * This ensures diary entries are still displayed even if the image cannot be fetched
 */
export function useFarmerImageUpload(imageUploadId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.farmerImageUploads.detail(imageUploadId ?? ""),
    queryFn: async (): Promise<FarmerImageUpload | null> => {
      if (!imageUploadId) {
        // This shouldn't be called when enabled is false, but return null for safety
        return null;
      }
      try {
        return await farmerImageUploadApi.getOne(imageUploadId);
      } catch (error) {
        console.warn(
          `Failed to fetch farmer image upload ${imageUploadId}:`,
          error
        );
        // Return null instead of undefined - React Query doesn't allow undefined
        return null;
      }
    },
    enabled: !!imageUploadId,
    retry: 1, // Only retry once
    retryOnMount: false, // Don't retry on mount if it already failed
    throwOnError: false, // Don't throw errors, handle them gracefully
  });
}

/**
 * Get a farmer image upload by ID from cache or fetch it
 * Returns the data directly (not wrapped in query object)
 * Returns undefined if the data is null (indicating fetch failed or no data)
 */
export function useFarmerImageUploadById(
  imageUploadId: string | undefined
): FarmerImageUpload | undefined {
  const { data } = useFarmerImageUpload(imageUploadId);
  // Convert null to undefined for compatibility with existing code
  return data === null ? undefined : data;
}

/**
 * Store farmer image upload in cache
 */
export function setFarmerImageUploadInCache(
  imageUpload: FarmerImageUpload,
  queryClient: QueryClient
): void {
  queryClient.setQueryData<FarmerImageUpload>(
    queryKeys.farmerImageUploads.detail(imageUpload.guid),
    imageUpload
  );
}

/**
 * Store multiple farmer image uploads in cache
 */
export function setFarmerImageUploadsInCache(
  imageUploads: FarmerImageUpload[],
  queryClient: QueryClient
): void {
  imageUploads.forEach((upload) => {
    queryClient.setQueryData<FarmerImageUpload>(
      queryKeys.farmerImageUploads.detail(upload.guid),
      upload
    );
  });
}

/**
 * Reset farmer image uploads query cache
 */
export function resetFarmerImageUploadsQueryCache(
  queryClient: QueryClient
): void {
  queryClient.removeQueries({
    queryKey: queryKeys.farmerImageUploads.all,
  });
}
